import type { CaseReducer, PayloadAction } from '@reduxjs/toolkit';

import type {
  DashboardTab,
  MarketAppWithTabs,
  TileBigNumber,
  WidgetV2
} from '@/features/dashboard/types';

import type { DashboardEditState } from '../dashboardEdit.types';
import {
  ensureDashboardDraft,
  ensureTabDraft,
  ensureUniqueAppId,
  generateUniqueTabName,
  isCurrentDashboard,
  markDashboardDirty
} from '../lib/dashboardEditValidation';
import { mapServerDataToDraft } from '../lib/mapping';

type State = DashboardEditState;

const createDashboardDraft: CaseReducer<State, PayloadAction<{ name: string }>> = (
  state,
  action
) => {
  if (!state.otherDashboardDrafts[action.payload.name]) {
    state.otherDashboardDrafts[action.payload.name] = { draftTabs: [], tabDrafts: {} };
  }
};

const initializeOtherDashboardDraft: CaseReducer<
  State,
  PayloadAction<{ dashboardName: string; serverData: DashboardTab }>
> = (state, action) => {
  const { dashboardName, serverData } = action.payload;

  if (isCurrentDashboard(state, dashboardName)) return;
  if (state.otherDashboardDrafts[dashboardName]) return;

  const { draftTabs, tabDrafts } = mapServerDataToDraft(serverData);
  state.otherDashboardDrafts[dashboardName] = { draftTabs, tabDrafts };
};

const moveAppToOtherDashboard: CaseReducer<
  State,
  PayloadAction<{ targetDashboardName: string; targetTabId: string; app: MarketAppWithTabs }>
> = (state, action) => {
  const { targetDashboardName, targetTabId, app } = action.payload;

  if (isCurrentDashboard(state, targetDashboardName)) {
    const targetDraft = state.tabDrafts[targetTabId];
    if (targetDraft) {
      targetDraft.marketTabApps.push(
        structuredClone(ensureUniqueAppId(app, targetDraft.marketTabApps))
      );
      state.isDirty = true;
      markDashboardDirty(state, state.dashboardName);
    }
    return;
  }

  const dashboardDraft = ensureDashboardDraft(state, targetDashboardName);
  const tabDraft = ensureTabDraft(dashboardDraft, targetTabId);
  tabDraft.marketTabApps.push(structuredClone(ensureUniqueAppId(app, tabDraft.marketTabApps)));
  markDashboardDirty(state, targetDashboardName);
};

const moveTileToOtherDashboard: CaseReducer<
  State,
  PayloadAction<{ targetDashboardName: string; targetTabId: string; tile: TileBigNumber }>
> = (state, action) => {
  const { targetDashboardName, targetTabId, tile } = action.payload;

  if (isCurrentDashboard(state, targetDashboardName)) {
    const targetDraft = state.tabDrafts[targetTabId];
    if (targetDraft) {
      targetDraft.tiles.push(structuredClone(tile));
      state.isDirty = true;
      markDashboardDirty(state, state.dashboardName);
    }
    return;
  }

  const dashboardDraft = ensureDashboardDraft(state, targetDashboardName);
  const tabDraft = ensureTabDraft(dashboardDraft, targetTabId);
  tabDraft.tiles.push(structuredClone(tile));
  markDashboardDirty(state, targetDashboardName);
};

const moveAppTabToOtherDashboard: CaseReducer<
  State,
  PayloadAction<{ targetDashboardName: string; targetTabId: string; tab: WidgetV2 }>
> = (state, action) => {
  const { targetDashboardName, targetTabId, tab } = action.payload;
  const newApp = { id: crypto.randomUUID(), tabs: [structuredClone(tab)] };

  if (isCurrentDashboard(state, targetDashboardName)) {
    const targetDraft = state.tabDrafts[targetTabId];
    if (targetDraft) {
      targetDraft.marketTabApps.push(newApp);
      state.isDirty = true;
      markDashboardDirty(state, state.dashboardName);
    }
    return;
  }

  const dashboardDraft = ensureDashboardDraft(state, targetDashboardName);
  const tabDraft = ensureTabDraft(dashboardDraft, targetTabId);
  tabDraft.marketTabApps.push(newApp);
  markDashboardDirty(state, targetDashboardName);
};

const moveTabToOtherDashboard: CaseReducer<
  State,
  PayloadAction<{
    targetDashboardName: string;
    tabId: string;
    tabName: string;
    marketTabApps: MarketAppWithTabs[];
    tiles: TileBigNumber[];
  }>
> = (state, action) => {
  const { targetDashboardName, tabId, tabName, marketTabApps, tiles } = action.payload;

  if (isCurrentDashboard(state, targetDashboardName)) return;

  const sourceTab = state.draftTabs.find(t => t.id === tabId);
  if (!sourceTab) return;

  const dashboardDraft = ensureDashboardDraft(state, targetDashboardName);
  dashboardDraft.draftTabs.push({ id: tabId, name: tabName, sourceName: null });
  dashboardDraft.tabDrafts[tabId] = { marketTabApps, tiles };
  markDashboardDirty(state, targetDashboardName);
};

const ensureTabExistsInOtherDashboard: CaseReducer<
  State,
  PayloadAction<{ dashboardName: string; tabId: string; tabName: string }>
> = (state, action) => {
  const { dashboardName, tabId, tabName } = action.payload;

  if (isCurrentDashboard(state, dashboardName)) {
    if (!state.tabDrafts[tabId]) {
      const uniqueName = generateUniqueTabName(state.draftTabs, tabName);
      state.draftTabs.push({ id: tabId, name: uniqueName, sourceName: null });
      state.tabDrafts[tabId] = { marketTabApps: [], tiles: [] };
      state.isDirty = true;
      markDashboardDirty(state, state.dashboardName);
    }
    return;
  }

  const dashboardDraft = ensureDashboardDraft(state, dashboardName);
  if (!dashboardDraft.tabDrafts[tabId]) {
    const uniqueName = generateUniqueTabName(dashboardDraft.draftTabs, tabName);
    dashboardDraft.draftTabs.push({ id: tabId, name: uniqueName, sourceName: null });
    dashboardDraft.tabDrafts[tabId] = { marketTabApps: [], tiles: [] };
  }
};

export const crossDashboardReducers = {
  createDashboardDraft,
  initializeOtherDashboardDraft,
  moveAppToOtherDashboard,
  moveTileToOtherDashboard,
  moveAppTabToOtherDashboard,
  moveTabToOtherDashboard,
  ensureTabExistsInOtherDashboard
};
